/**
 * PATH: packages/email-hub/src/_internal/hooks/useHostBottomDock.ts
 * FILE_ID: EMAILHUB-INTERNAL-HOOK-HOST-BOTTOM-DOCK-001
 * PURPOSE: Detect whether a HOST app (the main dashboard) is rendering its
 *          own fixed bottom dock over the Email Hub. The dashboard's
 *          MobileDock publishes `--mobile-dock-h` (e.g. "80px") as an inline
 *          style on <html> while it is mounted, and clears it otherwise.
 * WHY:     Operator (2026-06-17): "where is the email menu to navigate?".
 *          The Email Hub defaults `navPosition:'bottom'`, so embedded in the
 *          dashboard its folder bar (Inbox/Sent/Drafts/Binned…) is painted
 *          UNDER the host MobileDock (Kanban/Jobs/Settings…) — invisible and
 *          un-tappable. When a host dock exists the hub must move its folder
 *          nav off the bottom (we switch to the top folder strip) so it never
 *          collides. Standalone email.quotech.ai never sets this var → the
 *          hub keeps its existing bottom/rail behaviour unchanged.
 * FLOW:
 *   ┌──────────────────────┐   ┌────────────────────┐   ┌──────────────┐
 *   │ <html> style mutates │ → │ read --mobile-dock-h│ → │ boolean flag │
 *   └──────────────────────┘   └────────────────────┘   └──────────────┘
 * RELATED:
 *   - src/components/MobileDock/index.tsx (host): publishes --mobile-dock-h
 *   - ../../feature/EmailLayout.tsx (consumer): folder nav placement
 */

import { useEffect, useState } from 'react'

const HOST_DOCK_VAR = '--mobile-dock-h'

/** Pure: does this CSS-var value indicate a present host bottom dock? */
export function hasHostBottomDock(value: string | null | undefined): boolean {
  if (!value) return false
  const trimmed = value.trim()
  if (trimmed === '' || trimmed === '0' || trimmed === '0px') return false
  return true
}

function read(): boolean {
  if (typeof document === 'undefined') return false
  // Inline style (what MobileDock sets via root.style.setProperty) is the
  // authoritative, synchronously-observable signal — getComputedStyle would
  // also resolve inherited values that aren't a real host dock.
  return hasHostBottomDock(document.documentElement.style.getPropertyValue(HOST_DOCK_VAR))
}

/**
 * `true` while a host app renders a fixed bottom dock over the hub. Re-reads
 * whenever <html>'s style attribute changes (MobileDock mount/unmount on
 * viewport changes), so the folder nav re-places itself live.
 */
export function useHostBottomDock(): boolean {
  const [present, setPresent] = useState<boolean>(read)

  useEffect(() => {
    if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') {
      return
    }
    const root = document.documentElement
    const sync = () => setPresent(read())
    sync()
    const obs = new MutationObserver(sync)
    obs.observe(root, { attributes: true, attributeFilter: ['style'] })
    return () => obs.disconnect()
  }, [])

  return present
}

export default useHostBottomDock
